import * as THREE from "three";
import SceneGeometry from "../sceneObject/SceneGeometry";
import ExtrudedLoopAnnotationData from "../sceneObjectData/ExtrudedLoopAnnotationData";
import { SceneObjectStyle } from "../sceneObject/SceneObjectStyle";

import { Font } from "three/addons/loaders/FontLoader.js";
import FontProvider from "../../logic/FontProvider";
import { updateDoubleSidedText } from "../../utils/annotationUtils";
import { sceneUnitsFromMm } from "../../utils/scaler";
import { calculatePolygonAreaAndCentroid } from "../../utils/mathUtils";
import Constants from "../../constants";

// Shared logic for the 2D and 3D representations of an annotation extruded from a closed loop
// (Polygon/Rectangle/Circle) — see ExtrudedLoopSubGeometry2d (flat top-down projection) and
// ExtrudedLoopSubGeometry3d (wireframe/solid, depth-dependent) for the actual visuals. Both are
// held simultaneously by ExtrudedLoopCompoundGeometry, each on its own Annotations2d/3d layer, so
// each keeps its own independent solidGroup/textGroup/area-label mesh rather than sharing one.
// Owns: profile-point prep common to both (computeProfile), and the area label (built once per
// sub-geometry instance via updateAreaLabel/updateAreaLabelRotation) since both variants show one.
export default abstract class ExtrudedLoopSubGeometryBase<T extends ExtrudedLoopAnnotationData> extends SceneGeometry<T> {

    protected static readonly labelZOffset = sceneUnitsFromMm(5); // Lift text slightly above the surface it sits on to avoid z-fighting

    // Container transformed exactly like the extrusion's placement, holding everything else this sub-geometry draws
    protected readonly solidGroup: THREE.Group = new THREE.Group();

    protected readonly textGroup: THREE.Group = new THREE.Group();
    private readonly textFont: Font | undefined = FontProvider.getInstanceSync().arial;
    private textMaterial: THREE.MeshBasicMaterial;
    private areaLabelText: string = "";
    private areaLabelFontSize: number = 0;

    // World-space up direction the label text should read towards, kept in sync with the model's rotation offset
    private preferredAnnotationY: THREE.Vector3 = new THREE.Vector3(0, 1, 0);

    protected constructor(initialTextMaterial: THREE.MeshBasicMaterial) {
        super();
        this.textMaterial = initialTextMaterial;
        this.group.add(this.solidGroup);
    }

    // Leaf classes resolve a style to their own cached text material (see e.g. AreaAnnotationGeometry2d/3d).
    protected abstract getTextMaterialForStyle(style: SceneObjectStyle): THREE.MeshBasicMaterial;

    // Shared prep every subclass needs before drawing its own visual: the local-basis profile
    // points and whether the loop is closed yet (enough points, non-collinear) or should show as
    // an open preview instead (still being drawn). Returns undefined when there aren't even
    // enough points to show a preview line. Does NOT touch solidGroup's transform — 3D and 2D
    // each need a different one (3D: the shape's real placement transform; 2D: a flattened,
    // unrotated top-down transform — see each subclass's own updateGeometry()), so that's left
    // to the caller rather than applied here.
    protected computeProfile(data: T): {
        points2d: THREE.Vector2[];
        depth: number;
        isOpenPreview: boolean;
        centroid: THREE.Vector2;
    } | undefined {

        const points2d = data.getProfilePoints2d();
        if (points2d.length < 2) return undefined;

        const depth = Math.abs(data.depth);

        // Below 3 points, or a 3rd point still collinear with the first two, there's no real
        // closed loop yet — the caller should show an open preview line instead of a closed shape.
        const { area: shoelaceArea, centroid } = points2d.length >= 3
            ? calculatePolygonAreaAndCentroid(points2d)
            : { area: 0, centroid: new THREE.Vector2() };

        const isOpenPreview = points2d.length === 2 || Math.abs(shoelaceArea) < 1e-9;
        return { points2d, depth, isOpenPreview, centroid };
    }

    // area/volume are the raw values; showArea/showVolume are independent toggles (data.showAreaText,
    // data.showVolume) — either can be shown without the other, so turning one off must never hide
    // the other. When both are shown, they're rendered as a two-line "area\nvolume" string via
    // updateDoubleSidedText()'s existing multi-line support (`\n`-joined text, one merged+centered
    // geometry) — geometry.center() automatically keeps both lines equidistant from the label's
    // anchor point, so no separate position math is needed for the "area moves up to make room"
    // behavior. area comes from data.computeEnclosedArea() (the shape's exact formula), not from the
    // shoelace-over-points2d calculation computeProfile() does (which only sources the centroid) —
    // see ExtrudedLoopAnnotationData.computeEnclosedArea() for why those two aren't interchangeable.
    // fontSize is baked into the data by the creation command (see applyAreaBasedFontSize) rather
    // than derived here, so this always renders exactly the size the properties panel reports.
    // labelZ is supplied by the caller since 2D and 3D each want a different Z basis (see their
    // own updateGeometry()). Owns textGroup.visible entirely — hides it when neither is shown.
    protected updateAreaLabel(area: number, volume: number, showArea: boolean, showVolume: boolean, centroid: THREE.Vector2, labelZ: number, placementAxis: THREE.Vector3, fontSize: number): void {

        if (!showArea && !showVolume) {
            this.textGroup.visible = false;
            return;
        }

        const lines: string[] = [];
        if (showArea) {
            const isSmallArea = area < ExtrudedLoopAnnotationData.SMALL_AREA_THRESHOLD;
            lines.push(`${area.toFixed(isSmallArea ? 2 : 1)}m²`);
        }
        if (showVolume) {
            lines.push(`${volume.toFixed(2)}m³`);
        }
        const labelText = lines.join('\n');

        if (this.textFont && (this.textGroup.children.length === 0 || labelText !== this.areaLabelText || fontSize !== this.areaLabelFontSize)) {
            updateDoubleSidedText(labelText, this.textFont, fontSize, this.textGroup, this.textMaterial);
            this.areaLabelText = labelText;
            this.areaLabelFontSize = fontSize;
        }

        this.textGroup.position.set(centroid.x, centroid.y, labelZ);
        this.updateAreaLabelRotation(placementAxis);
        this.textGroup.visible = true;
    }

    // Text is authored flat in the local XY plane facing +Z, so only an in-plane (Z-axis) rotation is
    // needed to keep it reading upright in world space, regardless of the solid's own placement axes.
    // The world (not local) orientation of solidGroup must be used, since scene objects with a parentId
    // (e.g. attached to a storey) are added as children of that parent's rotated Object3D.
    private updateAreaLabelRotation(placementAxis: THREE.Vector3): void {
        // On a vertical-enough surface (e.g. a wall, or any sufficiently tilted surface — not just
        // a mathematically perfect vertical), the view-rotation-driven preferredAnnotationY doesn't
        // apply — always read the label upright against world +Z instead. This must use the exact
        // same threshold as AddExtrudedLoopAnnotationCmd.deriveRefDirection() uses to classify the
        // surface at creation time (Constants.HORIZONTAL_SURFACE_DOT_THRESHOLD): that method already
        // derives the placement's local Y axis to equal world Z whenever the surface isn't
        // "horizontal enough", so this must agree on exactly which surfaces count as such. A
        // stricter/mismatched threshold here (e.g. requiring axis.z to be exactly 0) would treat a
        // wall that isn't *perfectly* vertical (any small numeric noise in the raycast normal) as if
        // it were a floor, using the wrong up-reference and rendering the label rotated 90° off.
        const isVerticalSurface = Math.abs(placementAxis.z) < Constants.HORIZONTAL_SURFACE_DOT_THRESHOLD;
        const annotationY = isVerticalSurface ? new THREE.Vector3(0, 0, 1) : this.preferredAnnotationY;

        this.solidGroup.updateWorldMatrix(true, false);
        const worldQuaternion = this.solidGroup.getWorldQuaternion(new THREE.Quaternion());
        const localUp = annotationY.clone().applyQuaternion(worldQuaternion.invert());

        if (localUp.x === 0 && localUp.y === 0) {
            this.textGroup.rotation.z = 0;
            return;
        }

        this.textGroup.rotation.z = Math.atan2(-localUp.x, localUp.y);
    }

    onAnnotationDirectionChange(rotationOffset: number): void {
        const newAnnotationY = new THREE.Vector3(0, 1, 0);
        newAnnotationY.applyAxisAngle(new THREE.Vector3(0, 0, 1), rotationOffset);
        this.preferredAnnotationY = newAnnotationY;
        if (this.data)
            this.updateGeometry(this.data, true);
    }

    setStyle(style: SceneObjectStyle): void {
        if (this.style === style) return;

        const textMat = this.getTextMaterialForStyle(style);
        this.textMaterial = textMat;
        this.textGroup.traverse((child) => {
            if (child instanceof THREE.Mesh && child.name !== "HitBoxMesh") {
                child.material = textMat;
            }
        });

        this.style = style;
    }

    dispose(): void {
        this.textGroup.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.geometry.dispose();
            }
        });
        super.dispose();
    }
}
